import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { OnboardingBusiness } from './OnboardingBusiness';
import { OnboardingBranchStore } from './OnboardingBranchStore';
import {
  updateBranchStore,
  getBranchStores,
  createAddress,
} from '../../api/branchStores';
import { getToken } from '../../services/auth';

export const OnboardingController = () => {
  const userInfo = useSelector((state) => state.user);
  const storeInfo = useSelector((state) => state.store);

  const [currentStep, setCurrentStep] = useState(() => {
    const savedStep = localStorage.getItem('onboarding_current_step');
    return savedStep ? parseInt(savedStep) : 1;
  });

  const [currentBranchIndex, setCurrentBranchIndex] = useState(() => {
    const savedIndex = localStorage.getItem('onboarding_current_branch_index');
    return savedIndex ? parseInt(savedIndex) : 0;
  });

  const [onboardingData, setOnboardingData] = useState(() => {
    const savedData = localStorage.getItem('onboarding_form_data');
    return savedData
      ? JSON.parse(savedData)
      : {
          step1: {
            businessName:
              (storeInfo && storeInfo.store && storeInfo.store.name) || '',
            businessEmail: (userInfo && userInfo.email) || '',
            hasAdditionalLocations: false,
            additionalLocationsCount: 1,
          },
          step2: [],
        };
  });

  const [incompleteBranchStores, setIncompleteBranchStores] = useState([]);
  const [isLoadingBranchStores, setIsLoadingBranchStores] = useState(false);

  useEffect(() => {
    localStorage.setItem('onboarding_current_step', currentStep.toString());
    localStorage.setItem(
      'onboarding_current_branch_index',
      currentBranchIndex.toString(),
    );
    localStorage.setItem(
      'onboarding_form_data',
      JSON.stringify(onboardingData),
    );
  }, [currentStep, currentBranchIndex, onboardingData]);

  useEffect(() => {
    if (currentStep === 2) {
      fetchExistingBranchStores();
    }
  }, [currentStep]);

  const fetchExistingBranchStores = async () => {
    setIsLoadingBranchStores(true);
    try {
      const token = getToken();
      if (!token) {
        return;
      }

      const response = await getBranchStores({ token });

      let branchStores = [];
      if (response && response.results && Array.isArray(response.results)) {
        branchStores = response.results;
      } else if (Array.isArray(response)) {
        branchStores = response;
      } else {
        setIncompleteBranchStores([]);
        return;
      }

      if (branchStores.length > 0) {
        const incomplete = branchStores.filter(
          (store) => store.temporaryCreation === true,
        );
        setIncompleteBranchStores(incomplete);
        if (incomplete.length === 0) {
          setIncompleteBranchStores([]);
        }
      } else {
        setIncompleteBranchStores([]);
      }
    } catch (error) {
      setIncompleteBranchStores([]);
    } finally {
      setIsLoadingBranchStores(false);
    }
  };

  const handleStepComplete = (stepNumber, stepData) => {
    let updatedData;

    if (stepNumber === 1) {
      updatedData = {
        ...onboardingData,
        step1: stepData,
      };
      setOnboardingData(updatedData);
      setCurrentStep(2);
      setCurrentBranchIndex(0);
    } else if (stepNumber === 2) {
      if (incompleteBranchStores.length > 0) {
        handleBranchStoreComplete(stepData);
      } else {
        handleStepCompleteOldFlow(stepNumber, stepData);
      }
    }
  };

  const handleStepCompleteOldFlow = (stepNumber, stepData) => {
    let updatedData;

    if (stepNumber === 2) {
      const newStep2Data = [...onboardingData.step2];
      newStep2Data[currentBranchIndex] = stepData;

      updatedData = {
        ...onboardingData,
        step2: newStep2Data,
      };
      setOnboardingData(updatedData);

      const totalBranches = onboardingData.step1.additionalLocationsCount || 1;
      const nextBranchIndex = currentBranchIndex + 1;

      if (nextBranchIndex < totalBranches) {
        setCurrentBranchIndex(nextBranchIndex);
      } else {
        handleOnboardingComplete();
      }
    }
  };

  const handleBranchStoreComplete = async (stepData) => {
    try {
      const token = getToken();
      if (!token) {
        return;
      }

      const currentBranchStore = incompleteBranchStores[currentBranchIndex];
      if (!currentBranchStore) {
        return;
      }

      let addressId = null;
      if (stepData.fullAddress) {
        const addressData = new FormData();
        addressData.append('address', stepData.fullAddress);
        addressData.append('description', stepData.addressDescription || '');
        addressData.append('name', stepData.addressName || '');
        addressData.append('zipcode', stepData.zipcode || '');
        addressData.append('state', stepData.state || '');
        addressData.append('city', stepData.city || '');

        const addressResponse = await createAddress({
          token,
          params: addressData,
        });

        if (addressResponse.address && addressResponse.address.id) {
          addressId = addressResponse.address.id;
        } else {
          return;
        }
      }

      const branchStoreData = new FormData();
      branchStoreData.append('name', stepData.branchStoreName);
      branchStoreData.append('email', stepData.branchStoreEmail);
      branchStoreData.append('phone', stepData.branchStorePhone || '');
      branchStoreData.append('has_pickup', stepData.hasPickup);
      branchStoreData.append('has_delivery', stepData.hasDelivery);
      branchStoreData.append(
        'delivery_minimum_purchase_amount',
        stepData.deliveryMinAmount || '0',
      );
      branchStoreData.append(
        'pickup_minimum_purchase_amount',
        stepData.pickupMinAmount || '0',
      );
      branchStoreData.append('distance_radius', stepData.deliveryRadius || '0');
      branchStoreData.append('taxes', '0');
      branchStoreData.append('is_hq', currentBranchIndex === 0); // First store is HQ
      branchStoreData.append('use_only_exclusive_shopper', false);
      branchStoreData.append('temporary_creation', true);
      branchStoreData.append('store_id', (storeInfo && storeInfo.id) || '');
      if (addressId) {
        branchStoreData.append('address_id', addressId);
      }

      await updateBranchStore({
        id: currentBranchStore.id,
        token,
        params: branchStoreData,
      });

      branchStoreData.set('temporary_creation', false);
      await updateBranchStore({
        id: currentBranchStore.id,
        token,
        params: branchStoreData,
      });

      const nextBranchIndex = currentBranchIndex + 1;
      if (nextBranchIndex < incompleteBranchStores.length) {
        setCurrentBranchIndex(nextBranchIndex);
        await fetchExistingBranchStores();
      } else {
        handleOnboardingComplete();
      }
    } catch (error) {}
  };

  const handleStepBack = () => {
    if (currentStep === 2 && currentBranchIndex > 0) {
      setCurrentBranchIndex(currentBranchIndex - 1);
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      if (currentStep === 2) {
        setCurrentBranchIndex(0);
      }
    }
  };

  const handleOnboardingComplete = async () => {
    try {
      localStorage.removeItem('onboarding_current_step');
      localStorage.removeItem('onboarding_current_branch_index');
      localStorage.removeItem('onboarding_form_data');
      window.location.href = '/';
    } catch (error) {}
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <OnboardingBusiness
            onNext={(data) => handleStepComplete(1, data)}
            initialData={onboardingData.step1}
          />
        );
      case 2:
        if (isLoadingBranchStores) {
          return <div>Loading branch stores...</div>;
        }

        if (incompleteBranchStores.length > 0) {
          const totalBranches = incompleteBranchStores.length;
          const isLastBranch = currentBranchIndex === totalBranches - 1;
          const currentBranchStore = incompleteBranchStores[currentBranchIndex];

          return (
            <OnboardingBranchStore
              onNext={(data) => handleStepComplete(2, data)}
              onBack={handleStepBack}
              initialData={onboardingData.step2[currentBranchIndex] || {}}
              currentBranchIndex={currentBranchIndex}
              totalBranches={totalBranches}
              isLastBranch={isLastBranch}
              branchStoreId={currentBranchStore.id}
            />
          );
        } else {
          const totalBranches =
            onboardingData.step1.additionalLocationsCount || 1;
          const isLastBranch = currentBranchIndex === totalBranches - 1;

          return (
            <OnboardingBranchStore
              onNext={(data) => handleStepCompleteOldFlow(2, data)}
              onBack={handleStepBack}
              initialData={onboardingData.step2[currentBranchIndex] || {}}
              currentBranchIndex={currentBranchIndex}
              totalBranches={totalBranches}
              isLastBranch={isLastBranch}
              branchStoreId={null}
            />
          );
        }
      default:
        return (
          <OnboardingBusiness onNext={(data) => handleStepComplete(1, data)} />
        );
    }
  };

  return <>{renderCurrentStep()}</>;
};
